/**
 * DataGrid — the heavy-duty list surface for dense, operator-facing tables.
 *
 * Where {@link DataTable} is the lightweight list primitive, DataGrid is the
 * spreadsheet-grade grid: drag-resizable columns (double-click to reset),
 * freeze/unfreeze a column to pin it left, a sticky header, a sticky select
 * column (left) and sticky row-actions column (right), a column picker, server
 * sort, and both a client pager and a controlled cursor pager.
 *
 * Presentational + props-driven only — it never fetches. A page supplies typed
 * columns/rows and (optionally) controlled selection, sort, visibility and a
 * cursor pager; the grid owns all the chrome.
 *
 * No Popover/Menu primitive exists in the kit yet, so the column menu and column
 * picker use a small portaled popover defined at the bottom of this file.
 */
import {
  type Component,
  type JSX,
  For,
  Show,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
} from "solid-js";
import { Dynamic, Portal } from "solid-js/web";
import ArrowDown from "lucide-solid/icons/arrow-down";
import ArrowUp from "lucide-solid/icons/arrow-up";
import Check from "lucide-solid/icons/check";
import ChevronLeft from "lucide-solid/icons/chevron-left";
import ChevronRight from "lucide-solid/icons/chevron-right";
import Columns3 from "lucide-solid/icons/columns-3";
import Minus from "lucide-solid/icons/minus";
import MoreHorizontal from "lucide-solid/icons/ellipsis";
import Pin from "lucide-solid/icons/pin";
import PinOff from "lucide-solid/icons/pin-off";
import RefreshCw from "lucide-solid/icons/refresh-cw";
import { cn } from "./utils/cn";
import { focusRing } from "./utils/focus-ring";
import { Select, type SelectOption } from "./toolbar";
import { Skeleton } from "./skeleton";

type IconComponent = Component<{ size?: number | string; class?: string }>;

export interface GridColumn<Row> {
  key: string;
  header: string;
  width?: number;
  align?: "start" | "end";
  cell: (row: Row) => JSX.Element;
  /** Client-side sort accessor (uncontrolled grids). For SERVER sort, set `sortKey` and
   *  pass `sort`/`onSortChange` on the grid instead. */
  sortAccessor?: (row: Row) => string | number;
  /** Server-sort field for this column (controlled mode). Presence ⇒ the header is
   *  sortable; clicking it cycles asc→desc→off and emits `{ key: sortKey, dir }`. */
  sortKey?: string;
  frozen?: boolean;
  /** Hidden on first render but still toggleable in the column picker (smart defaults). */
  defaultHidden?: boolean;
}

/** A controlled sort selection: the server sort field + direction (or `null` for none). */
export interface GridSort {
  key: string;
  dir: "asc" | "desc";
}

/** Controlled cursor pagination — one keyset page per Next/Prev, server-sized by `pageSize`.
 *  Supplying this puts the grid in cursor mode: it renders exactly `rows` (no client slice)
 *  and the footer drives these handlers. There is no filtered total; `contextLabel` shows
 *  ambient context (e.g. the tenant catalog total) instead of a `1–N of M` range. */
export interface GridPager {
  pageSize: number;
  onPageSizeChange: (n: number) => void;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  contextLabel?: JSX.Element;
}

export interface DataGridProps<Row> {
  columns: GridColumn<Row>[];
  rows: Row[];
  getRowKey: (row: Row) => string;
  empty?: JSX.Element;
  onRefresh?: () => void;
  rowActionIcon?: IconComponent;
  onRowAction?: (row: Row) => void;
  /** Controlled row selection (pair with a bulk-action bar). Omit for internal state. */
  selected?: string[];
  onSelectionChange?: (keys: string[]) => void;
  /** Controlled SERVER sort. Supplying `onSortChange` puts the grid in controlled-sort
   *  mode: it stops sorting rows itself (the server already did) and routes header clicks
   *  here. Columns mark themselves sortable with `sortKey`. */
  sort?: GridSort | null;
  onSortChange?: (sort: GridSort | null) => void;
  /** Controlled cursor pagination — see {@link GridPager}. Omit for the internal client pager. */
  pager?: GridPager;
  /** Controlled column visibility (the hidden column keys). Supplying `onHiddenChange` puts the
   *  grid in controlled mode — the caller owns which columns are hidden (e.g. to persist a saved
   *  view). Omit both for internal state seeded from each column's `defaultHidden`. */
  hidden?: string[];
  onHiddenChange?: (keys: string[]) => void;
  class?: string;
}

const SELECT_W = 44;
const ACTIONS_W = 44;
const MIN_W = 80;
const DEFAULT_W = 160;
const PER_PAGE: SelectOption[] = [10, 25, 50, 100].map((n) => ({ value: String(n), label: String(n) }));

/* STICKY cells draw their separators with box-shadow (not border) so the line
   travels WITH the cell on scroll instead of detaching / doubling. Header cells
   draw their bottom border the same way so it stays put on vertical scroll.
   The sticky actions column owns a persistent INSET-left separator (visible in
   every scroll position, not borrowed from the last column); the last data
   column keeps its own border-r like every other column and drops it ONLY while
   flush against actions (scrolled fully right — see syncEdges + the cell
   render), so the two never stack into a doubled 2px line. */
const SH_HEAD_BOTTOM = "shadow-[inset_0_-1px_0_0_var(--c9-border)]";
const SH_HEAD_L = "shadow-[inset_0_-1px_0_0_var(--c9-border),inset_-1px_0_0_0_var(--c9-border)]"; // bottom + right
const SH_HEAD_R = "shadow-[inset_0_-1px_0_0_var(--c9-border),inset_1px_0_0_0_var(--c9-border)]"; // bottom + own left
const SH_BODY_L = "shadow-[inset_-1px_0_0_0_var(--c9-border)]"; // right
const SH_BODY_R = "shadow-[inset_1px_0_0_0_var(--c9-border)]"; // own left (last data col drops border-r only when flush, so never doubles)

export function DataGrid<Row>(props: DataGridProps<Row>): JSX.Element {
  const [widths, setWidths] = createSignal<Record<string, number>>(
    // eslint-disable-next-line solid/reactivity -- intentional one-time seed of local widths from initial columns; not meant to re-seed on later column changes
    Object.fromEntries(props.columns.map((c) => [c.key, c.width ?? DEFAULT_W])),
  );
  // eslint-disable-next-line solid/reactivity -- intentional one-time seed of default-hidden columns; later toggles live in this signal (uncontrolled mode)
  const [internalHidden, setInternalHidden] = createSignal<Set<string>>(new Set(props.columns.filter((c) => c.defaultHidden).map((c) => c.key)));
  const hidden = createMemo(() => (props.hidden !== undefined ? new Set(props.hidden) : internalHidden()));
  const toggleHidden = (key: string) => {
    const next = new Set(hidden());
    if (next.has(key)) next.delete(key);
    else next.add(key);
    props.onHiddenChange?.([...next]);
    if (props.hidden === undefined) setInternalHidden(next);
  };
  // eslint-disable-next-line solid/reactivity -- intentional one-time seed of frozen set from initial columns
  const [frozen, setFrozen] = createSignal<Set<string>>(new Set(props.columns.filter((c) => c.frozen).map((c) => c.key)));
  // Sort: controlled (server) when `onSortChange` is supplied, else this internal signal
  // drives the client-side `sortedRows` sort below.
  const [internalSort, setInternalSort] = createSignal<GridSort | null>(null);
  const controlledSort = () => props.onSortChange !== undefined;
  const sortState = createMemo<GridSort | null>(() => (controlledSort() ? (props.sort ?? null) : internalSort()));
  const cursorMode = () => props.pager !== undefined;
  // The active sort key for a column differs by mode: server `sortKey` vs the client `key`.
  const colSortId = (col: GridColumn<Row>) => (controlledSort() ? col.sortKey : col.key);
  const isSortable = (col: GridColumn<Row>) => (controlledSort() ? !!col.sortKey : !!col.sortAccessor);
  const setSortTo = (next: GridSort | null) => {
    if (controlledSort()) props.onSortChange!(next);
    else setInternalSort(next);
  };
  const [internalSel, setInternalSel] = createSignal<Set<string>>(new Set());
  const selected = createMemo(() => (props.selected !== undefined ? new Set(props.selected) : internalSel()));
  const applySel = (updater: (s: Set<string>) => Set<string>) => {
    const next = updater(selected());
    props.onSelectionChange?.([...next]);
    if (props.selected === undefined) setInternalSel(next);
  };
  const [hoverResize, setHoverResize] = createSignal<string | null>(null);
  const [resizing, setResizing] = createSignal<string | null>(null);
  const [scrollLeft, setScrollLeft] = createSignal(0);
  const [atRightEdge, setAtRightEdge] = createSignal(false);
  let scrollEl: HTMLDivElement | undefined;
  const [perPage, setPerPage] = createSignal(25);
  const [page, setPage] = createSignal(0);

  const visible = createMemo(() => props.columns.filter((c) => !hidden().has(c.key)));
  const width = (key: string) => widths()[key] ?? DEFAULT_W;
  const isFrozen = (key: string) => frozen().has(key);

  /* Track horizontal scroll + whether we're flush against the right edge. When
     the grid overflows AND is scrolled fully right, the last data column butts
     directly against the sticky actions column — only then does it drop its
     border-r (the actions column's own inset-left separator carries the single
     1px line) so the two never stack into 2px. Every other scroll position, and
     the non-overflow gap case, keeps the last column's border like the rest. */
  const syncEdges = () => {
    const el = scrollEl;
    if (!el) return;
    setScrollLeft(el.scrollLeft);
    const overflowing = el.scrollWidth > el.clientWidth + 1;
    setAtRightEdge(overflowing && el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  };
  createEffect(() => {
    widths();
    hidden();
    frozen(); // recompute when the column layout changes the scroll width
    syncEdges();
  });
  onMount(() => {
    syncEdges();
    window.addEventListener("resize", syncEdges);
    onCleanup(() => window.removeEventListener("resize", syncEdges));
  });

  const leftOffset = (key: string) => {
    let x = SELECT_W;
    for (const c of visible()) {
      if (c.key === key) break;
      if (isFrozen(c.key)) x += width(c.key);
    }
    return x;
  };
  const rightEdge = (key: string) => {
    let x = SELECT_W;
    for (const c of visible()) {
      x += width(c.key);
      if (c.key === key) break;
    }
    return x;
  };
  // eslint-disable-next-line solid/reactivity -- accessor; its reactive reads run when it's called inside JSX (a tracked scope)
  const frozenRight = () => SELECT_W + visible().filter((c) => isFrozen(c.key)).reduce((s, c) => s + width(c.key), 0);
  // Border x in wrapper coords; for a frozen column it tracks the (non-scrolling) sticky edge.
  const borderX = (key: string) => (isFrozen(key) ? leftOffset(key) + width(key) + scrollLeft() : rightEdge(key));

  const sortedRows = createMemo(() => {
    if (controlledSort()) return props.rows; // server already ordered this page
    const s = internalSort();
    if (!s) return props.rows;
    const col = props.columns.find((c) => c.key === s.key);
    if (!col?.sortAccessor) return props.rows;
    const acc = col.sortAccessor;
    return [...props.rows].sort((a, b) => {
      const av = acc(a);
      const bv = acc(b);
      const r = av < bv ? -1 : av > bv ? 1 : 0;
      return s.dir === "asc" ? r : -r;
    });
  });

  // In cursor mode the server returns exactly one page; render it verbatim. Otherwise
  // slice the client pager window.
  const pageCount = () => Math.max(1, Math.ceil(sortedRows().length / perPage()));
  const safePage = () => Math.min(page(), pageCount() - 1);
  const pagedRows = createMemo(() =>
    cursorMode() ? sortedRows() : sortedRows().slice(safePage() * perPage(), safePage() * perPage() + perPage()),
  );
  const rangeStart = () => (sortedRows().length === 0 ? 0 : safePage() * perPage() + 1);
  const rangeEnd = () => Math.min(sortedRows().length, rangeStart() + pagedRows().length - 1);

  const cycleSort = (col: GridColumn<Row>) => {
    if (!isSortable(col)) return;
    const id = colSortId(col)!;
    const cur = sortState();
    setSortTo(!cur || cur.key !== id ? { key: id, dir: "asc" } : cur.dir === "asc" ? { key: id, dir: "desc" } : null);
  };

  const startResize = (key: string, e: PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing(key);
    const startX = e.clientX;
    const startW = width(key);
    const onMove = (ev: PointerEvent) => setWidths((w) => ({ ...w, [key]: Math.max(MIN_W, startW + (ev.clientX - startX)) }));
    const onUp = () => {
      setResizing(null);
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.body.style.userSelect = "";
    };
    document.body.style.userSelect = "none";
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  };
  const resetWidth = (col: GridColumn<Row>) => setWidths((w) => ({ ...w, [col.key]: col.width ?? DEFAULT_W }));

  const allOnPage = () => pagedRows().length > 0 && pagedRows().every((r) => selected().has(props.getRowKey(r)));
  const someOnPage = () => pagedRows().some((r) => selected().has(props.getRowKey(r)));
  const toggleAll = () =>
    // eslint-disable-next-line solid/reactivity -- click handler; reactive reads run at event time inside the updater
    applySel((s) => {
      const n = new Set(s);
      const all = allOnPage();
      for (const r of pagedRows()) {
        if (all) n.delete(props.getRowKey(r));
        else n.add(props.getRowKey(r));
      }
      return n;
    });
  const toggleRow = (key: string) =>
    applySel((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });

  const stickyBg = "bg-[color:var(--c9-surface)] group-hover:bg-[color:var(--c9-surface-hovered)]";
  const headBase =
    "sticky top-0 z-20 h-9 whitespace-nowrap bg-[color:var(--c9-surface)] px-150 text-left align-middle text-body-s font-[630] text-[color:var(--c9-text-subtle)]";

  const lineKey = () => resizing() ?? hoverResize();
  const iconBtn = cn(
    "inline-flex size-8 items-center justify-center rounded-[3px] text-[color:var(--c9-icon-subtle)]",
    "hover:bg-[var(--c9-bg-neutral-subtle-hovered)] active:bg-[var(--c9-bg-neutral-subtle-pressed)] disabled:cursor-not-allowed disabled:opacity-40",
    focusRing,
  );

  return (
    // The outer wrapper fills the available height (the call site's `flex-1`) but stays transparent;
    // the bordered box inside SIZES TO CONTENT, capped at that height (`max-h-full`). Few rows ⇒ the
    // box shrinks and the footer rises to sit right under the last row (the empty space falls BELOW
    // the box); many rows ⇒ the box hits the cap, the body scrolls, and the footer pins to the bottom
    // as before. Centralised here, so every call site inherits it.
    <div class={cn("flex min-h-0 flex-col", props.class)}>
      <div class="flex min-h-0 max-h-full flex-col overflow-hidden rounded-large border border-[color:var(--c9-border)] bg-[color:var(--c9-surface)]">
        <div ref={(el) => (scrollEl = el)} class="min-h-0 flex-1 overflow-auto" onScroll={syncEdges}>
          <div class="relative min-h-full">
            <table class="w-full table-fixed border-collapse text-body">
              <colgroup>
                <col style={{ width: `${SELECT_W}px` }} />
                <For each={visible()}>{(c) => <col style={{ width: `${width(c.key)}px` }} />}</For>
                <col />
                <col style={{ width: `${ACTIONS_W}px` }} />
              </colgroup>

              <thead>
                <tr>
                  <th class={cn(headBase, "sticky left-0 z-30 px-0", SH_HEAD_L)} style={{ "z-index": 30 }}>
                    <div class="flex items-center justify-center">
                      <GridCheckbox checked={allOnPage()} indeterminate={!allOnPage() && someOnPage()} onChange={toggleAll} label="Select all" />
                    </div>
                  </th>

                  <For each={visible()}>
                    {(col, i) => {
                      const fz = () => isFrozen(col.key);
                      // Guard the null sort explicitly: in controlled mode a non-sortable column
                      // has `colSortId === undefined`, which would match a null `sortState()?.key`
                      // (undefined === undefined) and then read `.dir` off null. Require an active sort.
                      const sorted = () => {
                        const s = sortState();
                        return s != null && s.key === colSortId(col);
                      };
                      const last = () => i() === visible().length - 1;
                      return (
                        <th
                          class={cn(headBase, "group/h", fz() ? cn("z-30", SH_HEAD_L) : cn(SH_HEAD_BOTTOM, (!last() || !atRightEdge()) && "border-r border-[color:var(--c9-border)]"))}
                          style={fz() ? { left: `${leftOffset(col.key)}px`, "z-index": 30 } : undefined}
                        >
                          <div class="flex items-center gap-100">
                            <button type="button" onClick={() => cycleSort(col)} class={cn("min-w-0 flex-1 truncate text-left", isSortable(col) && "cursor-pointer hover:text-[color:var(--c9-text)]")}>
                              {col.header}
                            </button>
                            <Show when={sorted()}>
                              <Dynamic component={sortState()!.dir === "asc" ? ArrowUp : ArrowDown} size={12} class="shrink-0 text-[color:var(--c9-text-subtle)]" />
                            </Show>
                            <span class="shrink-0 opacity-0 transition-opacity group-hover/h:opacity-100 focus-within:opacity-100">
                              <ColumnMenu
                                sortable={isSortable(col)}
                                frozen={fz()}
                                onSortAsc={() => setSortTo({ key: colSortId(col)!, dir: "asc" })}
                                onSortDesc={() => setSortTo({ key: colSortId(col)!, dir: "desc" })}
                                onToggleFreeze={() =>
                                  setFrozen((f) => {
                                    const n = new Set(f);
                                    if (n.has(col.key)) n.delete(col.key);
                                    else n.add(col.key);
                                    return n;
                                  })
                                }
                              />
                            </span>
                          </div>
                        </th>
                      );
                    }}
                  </For>

                  <th class={cn(headBase, "z-20", SH_HEAD_BOTTOM)} />
                  <th class={cn(headBase, "sticky right-0 z-30 px-0", SH_HEAD_R)} style={{ "z-index": 30 }}>
                    <div class="flex items-center justify-center">
                      <ConfigureColumns columns={props.columns} hidden={hidden()} onToggle={toggleHidden} />
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody>
                <Show
                  when={pagedRows().length > 0}
                  fallback={
                    <tr>
                      <td colSpan={visible().length + 3} class="px-150 py-600 text-center text-body text-[color:var(--c9-text-subtle)]">
                        {props.empty ?? "No results."}
                      </td>
                    </tr>
                  }
                >
                  <For each={pagedRows()}>
                    {(row) => {
                      const key = props.getRowKey(row);
                      return (
                        <tr class="group border-b border-[color:var(--c9-border)] last:border-b-0 hover:bg-[color:var(--c9-surface-hovered)]">
                          <td class={cn("px-0 align-middle sticky left-0 z-10", SH_BODY_L, stickyBg)}>
                            <div class="flex items-center justify-center">
                              <GridCheckbox checked={selected().has(key)} onChange={() => toggleRow(key)} label={`Select ${key}`} />
                            </div>
                          </td>

                          <For each={visible()}>
                            {(col, i) => {
                              const fz = () => isFrozen(col.key);
                              const last = () => i() === visible().length - 1;
                              return (
                                <td
                                  class={cn(
                                    "truncate px-150 py-100 align-middle text-[color:var(--c9-text)]",
                                    col.align === "end" && "text-right",
                                    fz() ? cn("sticky z-10", SH_BODY_L, stickyBg) : (!last() || !atRightEdge()) && "border-r border-[color:var(--c9-border)]",
                                  )}
                                  style={fz() ? { left: `${leftOffset(col.key)}px` } : undefined}
                                >
                                  {col.cell(row)}
                                </td>
                              );
                            }}
                          </For>

                          <td />
                          <td class={cn("px-0 align-middle sticky right-0 z-10", SH_BODY_R, stickyBg)}>
                            <Show when={props.onRowAction}>
                              <div class="flex items-center justify-center">
                                <button type="button" aria-label="Row actions" onClick={() => props.onRowAction!(row)} class={iconBtn}>
                                  <Dynamic component={props.rowActionIcon ?? MoreHorizontal} size={16} />
                                </button>
                              </div>
                            </Show>
                          </td>
                        </tr>
                      );
                    }}
                  </For>
                </Show>
              </tbody>
            </table>

            {/* Full-height resize zones — one per column, spanning header→last row (not
                the footer). Grab the border anywhere in the body. Frozen columns track
                their sticky edge with scrollLeft; non-frozen ones hide under the frozen
                region so they don't bleed over it. */}
            <For each={visible()}>
              {(col) => {
                const fz = () => isFrozen(col.key);
                const show = () => fz() || rightEdge(col.key) - scrollLeft() > frozenRight();
                return (
                  <Show when={show()}>
                    <div
                      onPointerDown={(e) => startResize(col.key, e)}
                      onDblClick={() => resetWidth(col)}
                      onPointerEnter={() => setHoverResize(col.key)}
                      onPointerLeave={() => setHoverResize((k) => (k === col.key ? null : k))}
                      class="absolute top-0 bottom-0 z-30 w-[9px] -translate-x-1/2 cursor-col-resize touch-none"
                      style={{ left: `${borderX(col.key)}px` }}
                      aria-hidden="true"
                    />
                  </Show>
                );
              }}
            </For>

            {/* Resize indicator line (sits over the body only; never the footer). */}
            <Show when={lineKey()}>
              <div
                class="pointer-events-none absolute top-0 bottom-0 z-40 w-[2px]"
                style={{ left: `${borderX(lineKey()!) - 1}px`, "background-color": resizing() ? "var(--c9-border-focused)" : "var(--c9-border-bold)" }}
                aria-hidden="true"
              />
            </Show>
          </div>
        </div>

        {/* Footer: rows-per-page (left) · range/selected (centred) · refresh + pager (right). */}
        <div class="grid h-10 shrink-0 grid-cols-3 items-center border-t border-[color:var(--c9-border)] px-150">
          <div class="flex items-center gap-100 justify-self-start">
            <span class="hidden text-body-s text-[color:var(--c9-text-subtle)] sm:inline">Rows per page</span>
            <Select
              value={String(cursorMode() ? props.pager!.pageSize : perPage())}
              options={PER_PAGE}
              onChange={(v) => (cursorMode() ? props.pager!.onPageSizeChange(Number(v)) : (setPerPage(Number(v)), setPage(0)))}
              label="Rows per page"
              class="w-[4.25rem]"
            />
          </div>

          <span class="justify-self-center truncate text-body-s font-[570] text-[color:var(--c9-text-subtle)]">
            {/* Cursor mode has no filtered total — show ambient context (caller-supplied),
                never a fabricated `1–N of M`. Selection still wins when rows are picked. */}
            <Show when={selected().size > 0} fallback={cursorMode() ? props.pager!.contextLabel : `${rangeStart()}–${rangeEnd()} of ${sortedRows().length}`}>
              {selected().size} selected
            </Show>
          </span>

          <div class="flex items-center gap-050 justify-self-end">
            <Show when={props.onRefresh}>
              <button type="button" aria-label="Refresh" onClick={() => props.onRefresh!()} class={iconBtn}>
                <RefreshCw size={14} />
              </button>
            </Show>
            <button type="button" aria-label="Previous page" disabled={cursorMode() ? !props.pager!.hasPrev : safePage() === 0} onClick={() => (cursorMode() ? props.pager!.onPrev() : setPage(safePage() - 1))} class={iconBtn}>
              <ChevronLeft size={16} />
            </button>
            <button type="button" aria-label="Next page" disabled={cursorMode() ? !props.pager!.hasNext : safePage() >= pageCount() - 1} onClick={() => (cursorMode() ? props.pager!.onNext() : setPage(safePage() + 1))} class={iconBtn}>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Reserves the grid chrome (bordered box, sticky-height header, N rows, footer) so the
 *  loaded grid drops in with no layout jump. Row heights match the live grid. */
export function DataGridSkeleton(props: { columns: number; rows?: number; class?: string }): JSX.Element {
  const cols = () => Array.from({ length: props.columns });
  const rows = () => Array.from({ length: props.rows ?? 8 });
  return (
    <div class={cn("flex min-h-0 flex-col", props.class)}>
      <div class="flex min-h-0 max-h-full flex-col overflow-hidden rounded-large border border-[color:var(--c9-border)] bg-[color:var(--c9-surface)]">
        {/* Header — matches the h-9 sticky header row. */}
        <div class="flex h-9 shrink-0 items-center gap-200 border-b border-[color:var(--c9-border)] px-150">
          <Skeleton class="h-3.5 w-4 shrink-0" />
          <For each={cols()}>{(_, i) => <Skeleton class={cn("h-3.5", i() === 0 ? "w-28" : "w-16")} />}</For>
        </div>
        {/* Body rows — px-150 py-100 like live cells. */}
        <div class="min-h-0 flex-1 overflow-hidden">
          <For each={rows()}>
            {() => (
              <div class="flex items-center gap-200 border-b border-[color:var(--c9-border)] px-150 py-100" style={{ height: "2.5rem" }}>
                <Skeleton class="size-4 shrink-0" />
                <For each={cols()}>{(_, i) => <Skeleton class={cn("h-4", i() === 0 ? "w-36" : "w-20")} />}</For>
              </div>
            )}
          </For>
        </div>
        {/* Footer — matches the h-10 footer. */}
        <div class="flex h-10 shrink-0 items-center justify-between border-t border-[color:var(--c9-border)] px-150">
          <Skeleton class="h-8 w-[4.25rem]" />
          <Skeleton class="h-4 w-24" />
          <Skeleton class="h-8 w-16" />
        </div>
      </div>
    </div>
  );
}

function GridCheckbox(props: { checked?: boolean; indeterminate?: boolean; onChange?: () => void; label?: string }): JSX.Element {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={props.indeterminate ? "mixed" : !!props.checked}
      aria-label={props.label}
      onClick={(e) => {
        e.stopPropagation();
        props.onChange?.();
      }}
      class={cn(
        "flex size-4 shrink-0 items-center justify-center rounded-xsmall border transition-colors",
        props.checked || props.indeterminate
          ? "border-transparent bg-[var(--c9-bg-brand-bold)] text-[color:var(--c9-text-inverse)]"
          : "border-[color:var(--c9-border-bold)] bg-[color:var(--c9-bg-input)]",
        focusRing,
      )}
    >
      <Show when={props.indeterminate} fallback={<Show when={props.checked}><Check size={12} stroke-width={3} /></Show>}>
        <Minus size={12} stroke-width={3} />
      </Show>
    </button>
  );
}

function ColumnMenu(props: { sortable: boolean; frozen: boolean; onSortAsc: () => void; onSortDesc: () => void; onToggleFreeze: () => void }): JSX.Element {
  return (
    <GridPopover
      width={220}
      trigger={({ toggle, setRef }) => (
        <button ref={setRef} type="button" aria-label="Column options" onClick={toggle} class={cn("inline-flex size-6 items-center justify-center rounded-[3px] text-[color:var(--c9-icon-subtle)] hover:bg-[var(--c9-bg-neutral-subtle-hovered)]", focusRing)}>
          <MoreHorizontal size={14} />
        </button>
      )}
    >
      {({ close }) => (
        <div class="py-050">
          <Show when={props.sortable}>
            <GridMenuItem
              onClick={() => {
                props.onSortAsc();
                close();
              }}
            >
              <ArrowUp size={16} /> Sort ascending
            </GridMenuItem>
            <GridMenuItem
              onClick={() => {
                props.onSortDesc();
                close();
              }}
            >
              <ArrowDown size={16} /> Sort descending
            </GridMenuItem>
          </Show>
          <GridMenuItem
            onClick={() => {
              props.onToggleFreeze();
              close();
            }}
          >
            <Dynamic component={props.frozen ? PinOff : Pin} size={16} />
            {props.frozen ? "Unfreeze column" : "Freeze column"}
          </GridMenuItem>
        </div>
      )}
    </GridPopover>
  );
}

function ConfigureColumns<Row>(props: { columns: GridColumn<Row>[]; hidden: Set<string>; onToggle: (key: string) => void }): JSX.Element {
  return (
    <GridPopover
      width={240}
      trigger={({ toggle, setRef }) => (
        <button ref={setRef} type="button" aria-label="Configure columns" onClick={toggle} class={cn("inline-flex size-8 items-center justify-center rounded-[3px] text-[color:var(--c9-icon-subtle)] hover:bg-[var(--c9-bg-neutral-subtle-hovered)]", focusRing)}>
          <Columns3 size={16} />
        </button>
      )}
    >
      {() => (
        <div class="py-050">
          <div class="px-150 pb-050 pt-050 text-body-s font-[700] uppercase tracking-[0.04em] text-[color:var(--c9-text-subtlest)]">Columns</div>
          <For each={props.columns}>
            {(c) => (
              <GridMenuItem multi selected={!props.hidden.has(c.key)} onClick={() => props.onToggle(c.key)}>
                {c.header}
              </GridMenuItem>
            )}
          </For>
        </div>
      )}
    </GridPopover>
  );
}

/** A menu row inside a {@link GridPopover}. `multi` renders a leading checkbox for
 *  multi-select lists (the column picker); otherwise the children lead with an icon. */
function GridMenuItem(props: { onClick: () => void; multi?: boolean; selected?: boolean; children: JSX.Element }): JSX.Element {
  return (
    <button
      type="button"
      role={props.multi ? "menuitemcheckbox" : "menuitem"}
      aria-checked={props.multi ? !!props.selected : undefined}
      onClick={props.onClick}
      class={cn(
        "flex w-full items-center gap-100 px-150 py-100 text-left text-body text-[color:var(--c9-text)]",
        "hover:bg-[var(--c9-surface-overlay-hovered)]",
        focusRing,
      )}
    >
      <Show when={props.multi}>
        <span
          class={cn(
            "flex size-4 shrink-0 items-center justify-center rounded-xsmall border transition-colors",
            props.selected ? "border-transparent bg-[var(--c9-bg-brand-bold)] text-[color:var(--c9-text-inverse)]" : "border-[color:var(--c9-border-bold)] bg-[color:var(--c9-bg-input)]",
          )}
        >
          <Show when={props.selected}>
            <Check size={12} stroke-width={3} />
          </Show>
        </span>
      </Show>
      {props.children}
    </button>
  );
}

/** A minimal anchored popover — the kit has no Popover primitive yet, so the grid
 *  ships its own. Portals a fixed-position panel under the trigger (right-aligned),
 *  and closes on outside pointerdown, Escape, scroll, or resize. */
function GridPopover(props: {
  width: number;
  trigger: (api: { toggle: () => void; setRef: (el: HTMLButtonElement) => void }) => JSX.Element;
  children: (api: { close: () => void }) => JSX.Element;
}): JSX.Element {
  const [open, setOpen] = createSignal(false);
  const [pos, setPos] = createSignal<{ top: number; left: number }>({ top: 0, left: 0 });
  let anchor: HTMLButtonElement | undefined;
  let panel: HTMLDivElement | undefined;

  const place = () => {
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    // Right-align the panel to the trigger, clamped into the viewport.
    const left = Math.max(8, Math.min(r.right - props.width, window.innerWidth - props.width - 8));
    setPos({ top: r.bottom + 4, left });
  };
  const close = () => setOpen(false);
  const toggle = () => {
    if (open()) {
      setOpen(false);
      return;
    }
    place();
    setOpen(true);
  };

  createEffect(() => {
    if (!open()) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (anchor?.contains(t) || panel?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener("pointerdown", onDown, true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    onCleanup(() => {
      document.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    });
  });

  return (
    <>
      {props.trigger({ toggle, setRef: (el) => (anchor = el) })}
      <Show when={open()}>
        <Portal>
          <div
            ref={panel}
            role="menu"
            class="fixed overflow-hidden rounded-medium border border-[color:var(--c9-border)] bg-[color:var(--c9-surface-overlay)] shadow-[var(--c9-shadow-overlay)]"
            style={{ top: `${pos().top}px`, left: `${pos().left}px`, width: `${props.width}px`, "z-index": "var(--c9-z-popup)" }}
          >
            {props.children({ close })}
          </div>
        </Portal>
      </Show>
    </>
  );
}
